<template>
  <div class="tw-rounded-sm tw-bg-white tw-px-4 tw-py-3">
    <div class="tw-flex">
      <div class="tw-grow tw-pb-2">
        <div class="tw-text-lg tw-font-semibold">
          {{ t("solution-licenses-title") }}
        </div>
        <div
          class="tw-text-xs tw-font-medium tw-uppercase tw-text-primary-grey-400"
        >
          {{ t("solution-licenses-value") }}
        </div>
      </div>
      <span
        v-tippy="tooltip"
        class="tw-text-primary-grey-200"
        :data-test-id="`${tier}-usage-per-solution-info-icon`"
      >
        <cb-icon-information :width="18" />
      </span>
    </div>
    <div>
      <template v-if="isLoading">
        <div v-for="i in [0, 1, 2, 3]" :key="i" class="tw-mb-1.5">
          <div class="tw-flex tw-flex-row tw-justify-between tw-mb-1">
            <div
              class="tw-animate-pulse tw-rounded-sm tw-bg-primary-grey-50 tw-h-3 tw-w-2/5"
            />
          </div>
          <div class="tw-h-3 tw-w-full tw-bg-primary-grey-50 tw-rounded">
            <div
              class="tw-animate-pulse tw-rounded-sm tw-bg-primary-grey-50 tw-h-1 tw-w-full"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <div
          v-for="(license, idx) in transformedLicensesList"
          :key="idx"
          class="tw-mb-2"
        >
          <div class="tw-flex tw-flex-row tw-gap-3">
            <div
              class="tw-w-8 tw-h-8 tw-p-0.5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-place-self-center"
            >
              <component :is="getIcon(license.type)" />
            </div>
            <div class="tw-flex-1">
              <div class="tw-flex tw-flex-row tw-justify-between tw-mb-1">
                <div class="tw-text-xs tw-font-semibold">
                  {{ $t(getLegendName(license.type)) }}
                </div>
                <div v-if="license.created_at" class="tw-text-xs">
                  {{ license.seats_used ?? 0 }}/{{
                    license.seats_purchased ?? 0
                  }}
                </div>
                <a
                  v-else
                  class="tw-text-xs tw-text-primary-grey-700 tw-flex tw-items-center tw-gap-1"
                  target="_blank"
                  :href="getDocumentationLink(license.type)"
                  :data-test-id="`${tier}-${getDataTestIdType(license.type)}-learn-more-link`"
                >
                  {{ $t(getLearnMoreText(license.type)) }}
                  <cb-icon-external-link :width="16" />
                </a>
              </div>
              <div
                class="tw-mb-1 tw-h-3 tw-w-full tw-bg-primary-grey-50 tw-rounded-full"
              >
                <div
                  class="tw-h-3 tw-rounded-full"
                  :class="lineColourBasedOnService(license)"
                  :style="{
                    width: widthBasedOnService(license),
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import { SERVICE_ICON } from "@/utils/services";
import { SupportedLicense, SupportedService } from "./UsagePerSolution.types";
import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks";
import {
  LicenceWithSeats,
  ResellerOrganisationResponse,
} from "@/types/licenceSolutions.ts";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useFlag } from "@/composables/useFlags/useFlag";
import { useIsMspRoute } from "@/composables/useIsMspRoute";

const { isMspRoute } = useIsMspRoute();

const tier = isMspRoute.value ? "msp" : "customer";

const { t } = useI18n();
const authStore = useAuthenticationStore();
const licenceStore = useLicenceStore();
const { collection } = storeToRefs(licenceStore);
const { isFeatureAvailable } = useFlag("DASHBOARD_EMAIL_ARCHIVING_CARD");
const isLoading = ref<boolean>(true);
const tooltip = ref({
  content: t("solution-licenses-tooltip"),
  placement: "top",
});
const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);

const services: SupportedService[] = [
  "EmailSecurity",
  "PhishingSimulation",
  "SecurityAwarenessTraining",
  "ArcTitan",
];

const licensesList = computed<LicenceWithSeats[]>(
  () =>
    (collection.value as ResellerOrganisationResponse | null)?.licences ?? [],
);
const transformedLicensesList = computed<SupportedLicense[]>(() => {
  if (!licensesList.value) {
    return [];
  }

  const licensesObject: Record<string, LicenceWithSeats> =
    licensesList.value.reduce((acc, cur) => {
      return { ...acc, [cur.solution_name]: { ...cur } };
    }, {});

  return services.map((service) => ({
    ...licensesObject[service],
    type: service,
  }));
});

const getLicenses = async () => {
  isLoading.value = true;
  try {
    await licenceStore.listAllLicences({
      uuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
    });
  } finally {
    isLoading.value = false;
  }
};
getLicenses();

const widthBasedOnService = (license: SupportedLicense) => {
  const seatsPurchased = license.seats_purchased ?? 0;
  const seatsUsed = license.seats_used ?? 0;

  if (!seatsPurchased && !seatsUsed) {
    return 0;
  }
  if (seatsUsed > seatsPurchased) {
    return "100%";
  }

  const percentage = (seatsUsed / seatsPurchased) * 100;

  return `${percentage}%`;
};

const lineColourBasedOnService = (license: SupportedLicense) => {
  const seatsPurchased = license.seats_purchased ?? 0;
  const seatsUsed = license.seats_used ?? 0;
  const threshold = 0.8 * seatsPurchased;
  const lineClass =
    seatsUsed >= threshold ? "tw-bg-[#fc842d]" : "tw-bg-primary-grey-300";
  return seatsUsed > seatsPurchased ? "tw-bg-[#d23f3f]" : lineClass;
};

const getLegendName = (type: SupportedService) =>
  ({
    EmailSecurity: "email-security-solution",
    PhishingSimulation: "phishing-simulation-label",
    SecurityAwarenessTraining: "sat-label",
    ArcTitan: "email-archiving-label",
  })[type];

const getLearnMoreText = (type: SupportedService) =>
  ({
    EmailSecurity: "no-sat",
    PhishingSimulation: "no-sat",
    SecurityAwarenessTraining: "no-sat",
    ArcTitan: "no-sat",
  })[type];

const getDataTestIdType = (type: SupportedService) =>
  ({
    EmailSecurity: "email-security-solution-licences",
    PhishingSimulation: "phishing-simulation-solution-licences",
    SecurityAwarenessTraining: "sat-solution-licences",
    ArcTitan: "email-archiving-solution-licences",
  })[type];

const getIcon = (type: SupportedService) =>
  ({
    EmailSecurity: SERVICE_ICON.PHISH_SERVICE,
    PhishingSimulation: SERVICE_ICON.PHISHING_SIMULATION,
    SecurityAwarenessTraining: SERVICE_ICON.SAT_SERVICE,
    ArcTitan: SERVICE_ICON.ARC_SERVICE,
  })[type];

const getDocumentationLink = (type: SupportedService) =>
  ({
    EmailSecurity: isFeatureAvailable.value
      ? EXTERNAL_DOCS_LINKS.EMAIL_SECURITY_MARKETING_PAGE
      : EXTERNAL_DOCS_LINKS.EMAIL_SECURITY,
    PhishingSimulation: EXTERNAL_DOCS_LINKS.TITANHQ_CONTACT_PAGE,
    SecurityAwarenessTraining: EXTERNAL_DOCS_LINKS.TITANHQ_CONTACT_PAGE,
    ArcTitan: isFeatureAvailable.value
      ? EXTERNAL_DOCS_LINKS.ARC_TITAN_MARKETING_PAGE
      : EXTERNAL_DOCS_LINKS.ARC_TITAN,
  })[type];
</script>
